import { Data, List, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { TextConstants } from '../../../Common/TextConstants';
import FileGroupingFileSpecImportProceduresView from '../../FileGroupingFileSpecImportProcedures/Views/FileGroupingFileSpecImportProceduresView';
import FileSpecConfigurationView from '../../FileSpecConfigurations/Views/FileSpecConfigurationView';
import ServiceProviderFileGroupingsView from '../../ServiceProviders/Views/ServiceProviderFileGroupingsView';
import ManageFileGroupingFileSpecComponentVM from '../Components/ManageFileGroupingFileSpecComponentVM';
import CreateFileGroupingFileSpecCommand from '../Models/Commands/CreateFileGroupingFileSpecCommand';
import UpdateFileGroupingFileSpecCommand from '../Models/Commands/UpdateFileGroupingFileSpecCommand';
import FileGroupingFileSpecLookup from '../Models/Lookups/FileGroupingFileSpecLookup';
import FileTypeLookup from '../Models/Lookups/FileTypeLookup';
import FileGroupingFileSpecSearchCriteria from '../Models/Queries/FileGroupingFileSpecSearchCriteria';

@NeoModel
export default class FileGroupingFileSpecVM extends Views.ViewModelBase {
    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        public fileGroupingFileSpecApiClient = AppService.get(Types.ApiClients.FileGroupingFileSpecApiClient),
        public serviceProviderApiClient = AppService.get(Types.ApiClients.ServiceProviderApiClient),
        private navigation = AppService.get(Types.Neo.Routing.NavigationHelper),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {
            super(taskRunner);
    };

    public fileGroupingID: number | null = null;
    public brokerName: string |null = null;
    public fileSpecID: number | null = null;
    public backURL: string | null = null;
    public fileGroupingFileSpecSearchCriteria = new FileGroupingFileSpecSearchCriteria();
    public fileSpecDropdownList = new List(FileTypeLookup);
    public manageFileGroupingFileSpecComponentVM = new ManageFileGroupingFileSpecComponentVM();
    public showManageFileGroupingFileSpecComponent: boolean = false;

    public pageManager = new Data.PageManager(this.fileGroupingFileSpecSearchCriteria, FileGroupingFileSpecLookup,
        this.fileGroupingFileSpecApiClient.getFileGroupingFileSpecList, {
            pageSize: 10,
            sortAscending: false,
            fetchInitial: false,
            initialTaskRunner: this.taskRunner
        });

    public async initialise(broker?: string, fileGroupingID?: number, fileSpecID?: number, backURL?: string) {
        this.fileGroupingID = Number(fileGroupingID);
        this.brokerName = String(broker) === "null" ? "" : String(broker);
        this.fileSpecID = isNaN(fileSpecID!) ? null : Number(fileSpecID);
        this.backURL = String(backURL);

        await this.setInputVariables();
        await this.pageManager.refreshData();
    }

    public async addNewFileGroupingFileSpec() {
        this.showManageFileGroupingFileSpecComponent = true;
        await this.manageFileGroupingFileSpecComponentVM.initialise();
        this.manageFileGroupingFileSpecComponentVM.managedFileGroupingFileSpec = new FileGroupingFileSpecLookup();
    }

    onBack() {
        this.navigation.navigateInternal(this.backURL!);
    }

    public navigateToServiceProviders() {
        this.navigation.navigateToView(ServiceProviderFileGroupingsView);
    }

    public navigateToFileSpecs() {
        this.navigation.navigateToView(FileSpecConfigurationView);
    }

    public cancel() {
        this.manageFileGroupingFileSpecComponentVM.managedFileGroupingFileSpec = new FileGroupingFileSpecLookup();
        this.setIndicatorsToFalse();
    }

    public setIndicatorsToFalse() {
        this.showManageFileGroupingFileSpecComponent = false;
        this.manageFileGroupingFileSpecComponentVM.isFieldReadOnlyInd = false;
    }

    public async setInputVariables() {        
        var fileSpecListResponse = await this.fileGroupingFileSpecApiClient.getFileTypeListByCriteria("");
        this.fileSpecDropdownList.set(fileSpecListResponse.data);
        var fileTypeSearch = this.fileSpecDropdownList.find(x => x.staticFileSpecID === this.fileSpecID)?.propertiesConcatenated!;
        this.fileGroupingFileSpecSearchCriteria.staticServiceProviderFileGroupingID = this.fileGroupingID !== 0 ? this.fileGroupingID : null;
        this.fileGroupingFileSpecSearchCriteria.staticFileSpecID = this.fileSpecID !== 0 ? this.fileSpecID : null;
        this.fileGroupingFileSpecSearchCriteria.serviceProviderGroupingName = this.brokerName;
        this.fileGroupingFileSpecSearchCriteria.fileTypeConcatenated = fileTypeSearch !== "" ? fileTypeSearch : null;
    }

    public async editFileGroupingFileSpec(fileGroupingFileSpec: FileGroupingFileSpecLookup) {
        this.manageFileGroupingFileSpecComponentVM.managedFileGroupingFileSpec.mapFrom(fileGroupingFileSpec).toJSObject();
        this.manageFileGroupingFileSpecComponentVM.isFieldReadOnlyInd = true;
        await this.manageFileGroupingFileSpecComponentVM.initialise();
        this.showManageFileGroupingFileSpecComponent = true;
    }

    public async clearSearch() {
        this.fileGroupingFileSpecSearchCriteria.meta.searchCriteria.value = "";
        await this.pageManager.refreshData();
    }

    public async saveFileGroupingFileSpec() {
        this.taskRunner.run(async () => {
            var response = this.manageFileGroupingFileSpecComponentVM.managedFileGroupingFileSpec.staticServiceProviderFileGroupingFileSpecId > 0
                ? await this.fileGroupingFileSpecApiClient.updateFileGroupingFileSpec((this.mapFileGroupingFileSpecToUpdateCommand()!.toJSObject()))
                : await this.fileGroupingFileSpecApiClient.addNewFileGroupingFileSpec((this.mapFileGroupingFileSpecToCreateCommand()!.toJSObject()));

            if (response.data.success) {
                await this.pageManager.refreshData();
                NotifyUtils.addSuccess(TextConstants.Titles.Success, "Stockbroker file spec has been successfully saved.", NotificationDuration.Standard);
            }
            else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
            }
            this.manageFileGroupingFileSpecComponentVM.managedFileGroupingFileSpec = new FileGroupingFileSpecLookup();
            this.showManageFileGroupingFileSpecComponent = false;
        })
    }

    private mapFileGroupingFileSpecToCreateCommand() {
        var command = new CreateFileGroupingFileSpecCommand().mapFrom(this.manageFileGroupingFileSpecComponentVM.managedFileGroupingFileSpec);
        return command;
    }

    private mapFileGroupingFileSpecToUpdateCommand() {
        var command = new UpdateFileGroupingFileSpecCommand().mapFrom(this.manageFileGroupingFileSpecComponentVM.managedFileGroupingFileSpec);
        return command;
    }

    public viewImportProcessesForBroker(item: FileGroupingFileSpecLookup) {
        this.navigation.navigateToView(FileGroupingFileSpecImportProceduresView, {fileGroupingFileSpecID: item.staticServiceProviderFileGroupingFileSpecId, fileGroupingID: item.staticServiceProviderFileGroupingId, staticFileSpecID: item.staticFileSpecId});
    }
}